import React from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import DataSource from "@/pages/DataSource";
import Dashboard from "@/pages/Dashboard";
import AuthCallback from "@/pages/AuthCallback";
import { fetchCurrentUser, logout } from "@/lib/api";
import { Loader2 } from "lucide-react";
import HppyfxMark from "@/components/HppyfxMark";

// Synchronous hash check to avoid race with AuthProvider /me call
const hasAuthCallback = () =>
  typeof window !== "undefined" && window.location.hash.includes("session_id=");

export default function App() {
  const [authState, setAuthState] = React.useState(
    hasAuthCallback() ? "callback" : "checking"
  );
  const [user, setUser] = React.useState(null);
  const [dataset, setDataset] = React.useState(null);

  // Check for an existing session cookie on first render.
  React.useEffect(() => {
    if (authState !== "checking") return;
    (async () => {
      try {
        const u = await fetchCurrentUser();
        setUser(u);
        setAuthState("authed");
      } catch {
        setAuthState("unauthed");
      }
    })();
  }, [authState]);

  const handleAuthCallbackDone = (u) => {
    if (u) {
      setUser(u);
      setAuthState("authed");
    } else {
      setAuthState("unauthed");
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } catch {
      // ignore
    }
    setUser(null);
    setDataset(null);
    setAuthState("unauthed");
  };

  if (authState === "callback") {
    return (
      <>
        <AuthCallback onAuthed={handleAuthCallbackDone} />
        <Toaster position="top-right" theme="light" richColors closeButton />
      </>
    );
  }

  if (authState === "checking") {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-white"
        data-testid="auth-checking"
      >
        <div className="flex flex-col items-center gap-6">
          <HppyfxMark variant="default" className="h-10" />
          <div className="flex items-center gap-3 text-gray-600 text-sm">
            <Loader2 className="w-4 h-4 animate-spin text-[#5E3BFF]" strokeWidth={1.5} />
            Loading...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {authState !== "authed" || !user ? (
        <Login />
      ) : !dataset ? (
        <DataSource user={user} onLoaded={setDataset} onSignOut={handleSignOut} />
      ) : (
        <Dashboard
          data={dataset}
          user={user}
          onReset={() => setDataset(null)}
          onSignOut={handleSignOut}
        />
      )}
      <Toaster position="top-right" theme="light" richColors closeButton />
    </div>
  );
}
